/* Premier Courses — enrol CTAs land on the cart, not straight on checkout.

   Every enrol CTA in the theme is a Shopify cart permalink (/cart/<variant>:<qty>).
   A permalink ALWAYS redirects to checkout; `return_to` is accepted into the URL
   and then ignored (verified against the live store), so there is no server-side
   way to make one stop at the cart. Routing to the review step has to happen on
   the click.

   This is one delegated listener rather than ~70 edited hrefs. The CTAs live
   across 11 JSON templates, two hardcoded links in course-enroll.liquid, and
   whatever pc-enroll.js writes into the rail when a tier radio changes.
   Intercepting the click covers every one of them, including any added later,
   and keeps the destination decision in a single place.

   On mutual exclusivity: a permalink REPLACES the entire cart. That was
   invisible when buyers went straight to checkout, but now that they see the
   cart, silently wiping an unrelated exam-prep line would read as a bug. The
   actual requirement is narrower — the three packages of one course are
   mutually exclusive — so this swaps only other variants of the SAME product
   and leaves everything else in the cart alone.

   Anything unexpected (offline, 4xx, slow network) falls through to the
   untouched href, which still works. A failure costs the buyer a cart review
   step, never the sale.

   Single-locale store, so the permalink path is matched literally. If locale
   prefixes are ever enabled, this regex and the /cart redirect need the prefix. */
(function () {
  'use strict';

  var PERMALINK = /^\/cart\/(\d+):(\d+)$/;
  var inFlight = false;

  /* Never hijack a click the buyer meant to open elsewhere. */
  function plainClick(e) {
    return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
  }

  function asJson(res) {
    if (!res.ok) throw new Error(res.url + ' -> ' + res.status);
    return res.json();
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(asJson);
  }

  function getCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(asJson);
  }

  /* ── Header cart link ──────────────────────────────────────────────────
     header.liquid server-renders the count, which is right on an uncached
     render and is the no-JS answer — but Shopify's CDN caches that HTML, so a
     returning buyer can be served a page that shows no cart when they have
     one. Re-read the real count on load and correct it. */
  function paintCartLink(count) {
    var link = document.querySelector('[data-pc-cart-link]');
    if (!link) return;
    var badge = link.querySelector('[data-pc-cart-count]');
    if (badge) badge.textContent = count;
    link.hidden = count === 0;
    link.setAttribute('aria-label', 'Your cart, ' + count + ' item' + (count === 1 ? '' : 's'));
  }

  function hydrateCartLink() {
    // Nothing on the page depends on this, so let it wait for idle time.
    var run = function () {
      getCart()
        .then(function (cart) { paintCartLink(cart.item_count); })
        .catch(function () { /* leave the server-rendered state alone */ });
    };
    // Usually fires within tens of ms; the timeout is the worst case, and on a
    // cached page it is how long a buyer waits to see their cart reappear.
    if (window.requestIdleCallback) window.requestIdleCallback(run, { timeout: 1000 });
    else setTimeout(run, 300);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hydrateCartLink);
  } else {
    hydrateCartLink();
  }

  /* ── Cart line controls ────────────────────────────────────────────────
     Quantity steppers and remove, committed immediately so the Update cart
     button has nothing left to do.

     After each change the whole cart SECTION is re-rendered server-side via
     the Section Rendering API rather than patched in JS. The subtotal, the
     line prices, the item count and — the real reason — which cross-sell
     offers are still eligible are all decided in Liquid. Re-implementing that
     here would mean two sources of truth that drift. */
  var qtyTimer = null;
  var cartBusy = false;

  function cartSectionId() {
    var root = document.querySelector('.pc-cart');
    var wrap = root && root.closest ? root.closest('[id^="shopify-section-"]') : null;
    return wrap ? wrap.id.replace('shopify-section-', '') : null;
  }

  function rerenderCart() {
    var id = cartSectionId();
    if (!id) return Promise.reject(new Error('no section id'));
    return fetch('/cart?section_id=' + encodeURIComponent(id), {
      credentials: 'same-origin'
    })
      .then(asText)
      .then(function (html) {
        var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.pc-cart');
        var current = document.querySelector('.pc-cart');
        if (!fresh || !current) throw new Error('cart section markup not found');
        current.replaceWith(fresh);
      });
  }

  function asText(res) {
    if (!res.ok) throw new Error(res.url + ' -> ' + res.status);
    return res.text();
  }

  function setLineQty(line, quantity) {
    if (cartBusy) return;
    cartBusy = true;
    var row = document.querySelector('.pc-cart__item[data-line="' + line + '"]');
    if (row) row.setAttribute('aria-busy', 'true');

    post('/cart/change.js', { line: Number(line), quantity: Number(quantity) })
      .then(function (cart) {
        paintCartLink(cart.item_count);
        return rerenderCart();
      })
      .catch(function () {
        // Full reload is the honest fallback — never leave the page showing a
        // quantity the cart does not actually hold.
        window.location.assign('/cart');
      })
      .then(function () { cartBusy = false; });
  }

  document.addEventListener('click', function (e) {
    var step = e.target && e.target.closest ? e.target.closest('[data-pc-qty-step]') : null;
    if (!step) return;
    e.preventDefault();

    var line = step.getAttribute('data-pc-line');
    var input = document.querySelector('[data-pc-qty-input][data-pc-line="' + line + '"]');
    if (!input) return;

    var next = Math.max(0, (parseInt(input.value, 10) || 0) + Number(step.getAttribute('data-pc-qty-step')));
    input.value = next; // optimistic, so rapid clicks feel instant

    // Coalesce a burst of clicks into one request.
    clearTimeout(qtyTimer);
    qtyTimer = setTimeout(function () { setLineQty(line, next); }, 350);
  });

  document.addEventListener('change', function (e) {
    var input = e.target && e.target.closest ? e.target.closest('[data-pc-qty-input]') : null;
    if (!input) return;
    clearTimeout(qtyTimer);
    setLineQty(input.getAttribute('data-pc-line'), Math.max(0, parseInt(input.value, 10) || 0));
  });

  document.addEventListener('click', function (e) {
    if (!plainClick(e)) return;
    var rm = e.target && e.target.closest ? e.target.closest('[data-pc-remove]') : null;
    if (!rm) return;
    e.preventDefault();
    clearTimeout(qtyTimer);
    setLineQty(rm.getAttribute('data-pc-line'), 0);
  });

  document.addEventListener('click', function (e) {
    if (!plainClick(e)) return;

    var link = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!link) return;
    // pc-enroll.js flags a sold-out tier this way instead of removing the node.
    if (link.getAttribute('aria-disabled') === 'true') return;
    if (link.host && link.host !== window.location.host) return;

    var match = PERMALINK.exec(link.pathname);
    if (!match) return;

    e.preventDefault();
    if (inFlight) return; // double-click shouldn't double-add
    inFlight = true;

    var fallback = link.href;
    var variantId = Number(match[1]);
    var quantity = Number(match[2]) || 1;

    link.setAttribute('aria-busy', 'true');

    post('/cart/add.js', { id: variantId, quantity: quantity })
      .then(function (added) {
        // add.js echoes the line it created, which is where product_id comes from.
        return getCart().then(function (cart) {
          var updates = {};
          cart.items.forEach(function (item) {
            if (item.product_id === added.product_id && item.variant_id !== variantId) {
              updates[item.variant_id] = 0;
            }
          });
          return Object.keys(updates).length ? post('/cart/update.js', { updates: updates }) : null;
        });
      })
      .then(function () {
        window.location.assign('/cart');
      })
      .catch(function () {
        // Let the permalink do what it always did rather than strand the buyer.
        window.location.assign(fallback);
      });
  });
})();

/* ── Auto-apply the running promotion ──────────────────────────────────────
   A discount CODE cannot be put on a cart from Liquid or the AJAX API —
   Shopify applies codes at checkout and keeps them out of the cart object.
   The one supported route is /discount/<CODE>?redirect=<path>, which stores
   the code on the session and comes back. So the buyer never types it, and
   the cart can then show the real title and amount.

   Guards, because this navigates:
     • cart page only, and only when it holds something
     • skipped once a discount is already on the cart
     • one attempt per session per code, so an expired or non-applicable code
       cannot bounce the page in a loop
   An AUTOMATIC discount needs none of this — leave the setting blank and
   Shopify applies it server-side before the page is ever built. */
(function () {
  'use strict';

  function autoApplyDiscount() {
    var root = document.querySelector('[data-pc-cart]');
    if (!root) return;

    var code = (root.getAttribute('data-pc-auto-discount') || '').trim();
    if (!code) return;
    if (root.getAttribute('data-pc-discount-applied') === 'true') return;
    if (parseInt(root.getAttribute('data-pc-item-count'), 10) < 1) return;

    var key = 'pc-discount-tried:' + code;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch (e) {
      return;   // no sessionStorage means no loop guard — do not risk it
    }

    var back = window.location.pathname + window.location.search;
    window.location.replace('/discount/' + encodeURIComponent(code) +
                            '?redirect=' + encodeURIComponent(back));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoApplyDiscount);
  } else {
    autoApplyDiscount();
  }
})();
